import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoPersonOutline,
  IoPeopleOutline,
  IoBookOutline,
  IoTrophyOutline,
  IoCalendarOutline,
  IoPodiumOutline,
  IoArrowForwardOutline
} from 'react-icons/io5';
import './AdminDashboardPage.css';

// Every card goes somewhere: the number is the way in to the page behind it
const StatCard = ({ to, icon, value, label, sub }) => (
  <Link to={to} className="admin-stat-card">
    <div className="stat-icon" aria-hidden="true">{icon}</div>
    <div className="stat-content">
      <h3 className="admin-stat-value">{(value ?? 0).toLocaleString()}</h3>
      <p className="admin-stat-label">{label}</p>
      {sub && <p className="admin-stat-sub">{sub}</p>}
    </div>
    <div className="stat-action"><IoArrowForwardOutline aria-hidden="true" /></div>
  </Link>
);

const AdminDashboardPage = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDashboard();
      setDashboardStats(response.data.dashboardStats);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load dashboard statistics'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboardStats(); }, [loadDashboardStats]);

  if (loading && !dashboardStats) {
    return (
      <div className="admin-loading">
        <LoadingSpinner size="large" message="Loading dashboard..." />
      </div>
    );
  }

  if (error && !dashboardStats) {
    return (
      <div className="admin-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={loadDashboardStats} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  const stats = dashboardStats || {};
  const seasonStart = stats.seasonStart
    ? new Date(`${stats.seasonStart}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="admin-dashboard-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Admin Dashboard</h1>
        <p className="admin-page-description">
          {seasonStart ? `This season started ${seasonStart}. ` : ''}
          Season numbers match what the public site shows.
        </p>
      </div>

      <h2 className="dashboard-section-title">This season</h2>
      <div className="admin-stats-grid">
        <StatCard
          to="/admin/reports"
          icon={<IoCalendarOutline />}
          value={stats.seasonChaptersRead}
          label="Chapters read"
          sub="Logged chapters plus bulk counts"
        />
        <StatCard
          to="/admin/leaderboards"
          icon={<IoPodiumOutline />}
          value={stats.seasonReaders}
          label="Readers reading"
          sub="With any chapter this season"
        />
      </div>

      <h2 className="dashboard-section-title">All time</h2>
      <div className="admin-stats-grid">
        <StatCard to="/admin/users" icon={<IoPersonOutline />} value={stats.totalUsers} label="Accounts" />
        <StatCard to="/admin/readers" icon={<IoPeopleOutline />} value={stats.totalReaders} label="Active readers" />
        <StatCard to="/admin/reports" icon={<IoBookOutline />} value={stats.totalReports} label="Chapter reports" />
        <StatCard
          to="/admin/goals"
          icon={<IoTrophyOutline />}
          value={stats.activeGoals}
          label="Active site goals"
          sub={`${stats.totalGoals ?? 0} in total`}
        />
      </div>

      {stats.goals && stats.goals.length > 0 && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Site goals</h2>
            <Link to="/admin/goals" className="dashboard-card-link">Manage goals</Link>
          </div>
          <div className="goals-overview">
            {stats.goals.map((goal) => (
              <div key={goal.id} className="goal-item">
                <div className="goal-info">
                  <h4>{goal.name}</h4>
                  {goal.description && <p>{goal.description}</p>}
                </div>
                <div className="goal-progress">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.min(goal.progress ?? 0, 100)}
                    aria-label={goal.name}
                  >
                    {/* goal.progress is already a 0-100 percentage */}
                    <div
                      className="progress-fill"
                      style={{ width: `${Math.min(goal.progress ?? 0, 100)}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {goal.currentValue?.toLocaleString() ?? 0} / {(goal.effectiveTargetValue ?? goal.targetValue)?.toLocaleString()} {goal.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
